// src/sectors/pilates/members/modal/detail/MemberDetailModal.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { attendanceApi } from 'sectors/pilates/attendance/api/attendanceApi';
import { AttendanceMarkModal } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/AttendanceMarkModal';
import { PaymentEditModal } from 'sectors/pilates/members/components/paymentReceive/PaymentEditModal';
import { PaymentDeleteConfirmModal } from 'sectors/pilates/members/components/paymentReceive/PaymentDeleteConfirmModal';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { PillIcon } from 'shared/ui/soft/PillIcon';
import { useMemberDebt } from '../../hooks/useMemberDebt';
import { useMemberPayments } from './hooks/useMemberPayments';
import { useMemberPackages } from './hooks/useMemberPackages';

import { GeneralTab, PaymentsTab, PackageTab, AttendanceTab, ScheduleTab } from './tabs';
import {
  memberDisplayName,
  statusToneByLabel,
  debtTry,
  statusLabel,
} from './utils/memberDetailUtils';

// a11y helpers
function a11yProps(index) {
  return {
    id: `member-detail-tab-${index}`,
    'aria-controls': `member-detail-tabpanel-${index}`,
  };
}

export function MemberDetailModal({ open, onClose, member, initialTab = 0 }) {
  const theme = useMuiTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = React.useState(0);

  const memberId = member?.id;

  // ✅ tab bazlı fetch (spam keser)
  const paymentsState = useMemberPayments(memberId, open && tab === 1);
  // Genel sekmede aktif paket göstereceğiz diye: tab 0 veya tab 2'de paketleri aç
  const packagesState = useMemberPackages(memberId, open && (tab === 0 || tab === 2 || tab === 3));

  // payment modals
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [selectedPayment, setSelectedPayment] = React.useState(null);
  const [attendanceDetailSession, setAttendanceDetailSession] = React.useState(null);

  const openEditPayment = React.useCallback((p) => {
    setSelectedPayment(p);
    setEditOpen(true);
  }, []);

  const openDeletePayment = React.useCallback((p) => {
    setSelectedPayment(p);
    setDeleteOpen(true);
  }, []);

  const closePaymentModals = React.useCallback(() => {
    setEditOpen(false);
    setDeleteOpen(false);
    setSelectedPayment(null);
  }, []);

  const handleSavePayment = React.useCallback(
    async (paymentId, patch) => {
      await membersApi.updatePayment(paymentId, patch);
      await paymentsState.fetchPayments?.();
    },
    [paymentsState]
  );

  const handleDeletePayment = React.useCallback(
    async (paymentId) => {
      await membersApi.deletePayment(paymentId);
      await paymentsState.fetchPayments?.();
    },
    [paymentsState]
  );

  React.useEffect(() => {
    if (open) setTab(Number.isFinite(initialTab) ? initialTab : 0);
  }, [open, initialTab]);

  React.useEffect(() => {
    if (!open) setAttendanceDetailSession(null);
  }, [open]);

  // ✅ debt sadece Ödemeler tabında ve sadece 1 kere fetch edilsin
  const { loading: debtLoading, err: debtErr, debtSummary, fetchDebt } = useMemberDebt(memberId);
  const debtFetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open || !memberId) {
      debtFetchedRef.current = false;
      return;
    }
    // modal açıldığında resetle
    debtFetchedRef.current = false;
  }, [open, memberId]);

  React.useEffect(() => {
    if (!open || !memberId) return;
    if (tab !== 1) return; // ✅ sadece Ödemeler
    if (debtFetchedRef.current) return;

    debtFetchedRef.current = true;
    fetchDebt?.();
  }, [open, memberId, tab, fetchDebt]);

  if (!member) return null;

  // derived
  const name = memberDisplayName(member);
  const phone = member?.phone || '—';
  const planName = member?.activePlanName || member?.planName || '—';
  const debt = debtTry(member);
  const st = statusLabel(member);
  const statusTone = statusToneByLabel(st);

  // ✅ packagesState içinden ACTIVE paketi seç (shape farklıysa sadece bu satırı düzeltirsin)
  const packages =
    packagesState?.items || packagesState?.data || packagesState?.packages || packagesState || [];
  const activePackage = Array.isArray(packages)
    ? packages.find((p) => p?.status === 'ACTIVE') || null
    : null;

  // small typography tokens
  const typo = {
    title: { fontSize: fullScreen ? '1.02rem' : '1.10rem' },
    sub: { fontSize: fullScreen ? '0.73rem' : '0.80rem' },
    chip: { fontSize: fullScreen ? '0.70rem' : '0.74rem' },
    tab: { fontSize: fullScreen ? '0.74rem' : '0.86rem' },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: fullScreen
          ? {
              height: '100dvh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : undefined,
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          p: fullScreen ? 1.1 : 2,
          position: fullScreen ? 'sticky' : 'relative',
          top: 0,
          zIndex: 3,
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(18,20,26,.96), rgba(18,20,26,.86))'
              : 'linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.92))',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <MDBox sx={{ display: 'grid', gap: 0.85 }}>
          {/* Row-1 */}
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.1} sx={{ minWidth: 0 }}>
              <MDBox
                sx={{
                  width: fullScreen ? 38 : 44,
                  height: fullScreen ? 38 : 44,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(0,150,136,.14)',
                  border: `1px solid rgba(0,150,136,.24)`,
                  flex: '0 0 auto',
                }}
              >
                <Icon sx={{ fontSize: fullScreen ? 19 : 22, color: '#009688' }}>person</Icon>
              </MDBox>

              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    ...typo.title,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: fullScreen ? '68vw' : 'unset',
                  }}
                >
                  {name}
                </MDTypography>
              </MDBox>
            </MDBox>

            <IconButton
              onClick={onClose}
              aria-label="Kapat"
              sx={{
                borderRadius: '14px',
                border: `1px solid ${theme.palette.divider}`,
                width: 40,
                height: 40,
                background: theme.palette.background.paper,
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          {/* Row-2 Chips */}
          <MDBox sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <MDTypography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
              Durum:
            </MDTypography>
            <Chip
              size="small"
              label={st}
              variant="outlined"
              sx={{
                borderRadius: '999px',
                fontWeight: 900,
                background: statusTone,
                borderColor: 'rgba(0,0,0,.12)',
                '& .MuiChip-label': { px: 1.0, fontSize: typo.chip.fontSize },
              }}
            />

            <MDTypography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, ml: 0.5 }}>
              Borç:
            </MDTypography>
            <Chip
              size="small"
              label={debt > 0 ? `${debt} ₺` : '0 ₺'}
              variant="outlined"
              sx={{
                borderRadius: '999px',
                fontWeight: 900,
                borderColor: debt > 0 ? 'rgba(244,67,54,.35)' : 'rgba(76,175,80,.35)',
                background: debt > 0 ? 'rgba(244,67,54,.08)' : 'rgba(76,175,80,.08)',
                '& .MuiChip-label': { px: 1.0, fontSize: typo.chip.fontSize },
              }}
            />
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={
          fullScreen
            ? {
                p: 0,
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }
            : { p: 2 }
        }
      >
        <SoftCard
          sx={
            fullScreen
              ? {
                  p: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : { p: 0 }
          }
        >
          {/* Card header */}
          <MDBox
            sx={{
              px: fullScreen ? 1.4 : 2,
              pt: fullScreen ? 1.2 : 1.6,
              pb: fullScreen ? 0.7 : 1.1,
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <PillIcon icon="view_agenda" tone="blue" />

              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ ...typo.title, lineHeight: 1.1 }}
                >
                  Üye Detayı
                </MDTypography>

                <MDTypography
                  variant="caption"
                  sx={{
                    ...typo.sub,
                    color: theme.palette.text.primary,
                    opacity: 0.68,
                    fontWeight: 600,
                  }}
                >
                  Sekmelerden detayları görüntüleyin
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          <Divider />

          {/* Tabs */}
          <MDBox
            sx={{
              px: fullScreen ? 0.3 : 1.2,
              position: fullScreen ? 'sticky' : 'relative',
              top: 0,
              zIndex: 2,
              background: theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Üye detay sekmeleri"
              sx={{
                '& .MuiTabs-flexContainer': { gap: 0.1 },
                '& .MuiTab-root.Mui-selected': {
                  background:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,.08)'
                      : 'rgba(12, 252, 3, 0.06)',
                },
                '& .MuiTabs-indicator': { height: 0 },

                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 850,
                  minHeight: fullScreen ? 42 : 48,
                  px: fullScreen ? 1.0 : 1.4,
                  py: fullScreen ? 0.55 : 0.8,
                  borderRadius: '14px',
                  marginRight: 0.25,
                  fontSize: typo.tab.fontSize,
                  minWidth: fullScreen ? 98 : 120,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: fullScreen ? 0 : 0.8,
                },

                '& .MuiTab-iconWrapper': { marginBottom: '0px !important' },

                '& .MuiTab-root .MuiTab-iconWrapper + span': {
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: fullScreen ? 72 : 96,
                },
              }}
            >
              <Tab
                icon={<Icon sx={{ fontSize: 18 }}>person</Icon>}
                iconPosition="start"
                label="Genel"
                {...a11yProps(0)}
              />
              <Tab
                icon={<Icon sx={{ fontSize: 18 }}>payments</Icon>}
                iconPosition="start"
                label={fullScreen ? 'Ödeme' : 'Ödemeler'}
                {...a11yProps(1)}
              />
              <Tab
                icon={<Icon sx={{ fontSize: 18 }}>inventory_2</Icon>}
                iconPosition="start"
                label="Paket"
                {...a11yProps(2)}
              />
              <Tab
                icon={<Icon sx={{ fontSize: 18 }}>task_alt</Icon>}
                iconPosition="start"
                label="Yoklama"
                {...a11yProps(3)}
              />
              <Tab
                icon={<Icon sx={{ fontSize: 18 }}>calendar_month</Icon>}
                iconPosition="start"
                label="Program"
                {...a11yProps(4)}
              />
            </Tabs>
          </MDBox>

          <Divider />

          {/* Content */}
          <MDBox
            sx={
              fullScreen
                ? {
                    p: 1.25,
                    display: 'grid',
                    gap: 1.25,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }
                : { p: 2, display: 'grid', gap: 1.25 }
            }
          >
            <GeneralTab
              tab={tab}
              member={member}
              name={name}
              phone={phone}
              planName={planName}
              statusLabelText={st}
              activePackage={activePackage}
            />

            <PaymentsTab
              tab={tab}
              theme={theme}
              paymentsState={paymentsState}
              memberLabel={`${member?.name || ''} ${member?.surname || ''}`.trim() || 'Üye'}
              onEditPayment={openEditPayment}
              onDeletePayment={openDeletePayment}
              debt={debt}
              debtSummary={debtSummary}
              debtLoading={debtLoading}
              debtErr={debtErr}
              onRefreshDebt={fetchDebt}
            />

            <PackageTab tab={tab} theme={theme} packagesState={packagesState} memberId={memberId} />
            <AttendanceTab
              tab={tab}
              memberId={memberId}
              memberPackages={Array.isArray(packages) ? packages : []}
              onOpenSession={(row) => {
                if (!row?.sessionId) return;
                setAttendanceDetailSession({
                  id: row.sessionId,
                  dayISO: row.dayISO,
                  startMin: row.startMin,
                  trainerId: row.trainerId ?? null,
                  trainerName: row.trainerName || '--',
                });
              }}
            />
            <ScheduleTab tab={tab} memberId={memberId} />
          </MDBox>
        </SoftCard>
      </DialogContent>

      {/* Payment modals */}
      <PaymentEditModal
        open={editOpen}
        payment={selectedPayment}
        memberLabel={name}
        onClose={closePaymentModals}
        onSave={handleSavePayment}
      />

      <PaymentDeleteConfirmModal
        open={deleteOpen}
        payment={selectedPayment}
        memberLabel={name}
        onClose={closePaymentModals}
        onConfirm={handleDeletePayment}
      />

      <AttendanceMarkModal
        open={!!attendanceDetailSession}
        onClose={() => setAttendanceDetailSession(null)}
        session={attendanceDetailSession}
        trainersById={new Map()}
        fetchParticipants={async (sid) => {
          const res = await attendanceApi.getSessionParticipants(sid, 5);
          return res?.participants ?? [];
        }}
        onSave={async () => {}}
        onWeeklyRefresh={async () => {}}
      />
    </Dialog>
  );
}
